import React, { useContext, useEffect, useState } from 'react';
import { Button, Form, InputGroup, Modal, Spinner } from 'react-bootstrap';
import {
  FaCalendarAlt,
  FaChalkboardTeacher,
  FaEnvelope,
  FaEye,
  FaEyeSlash,
  FaGraduationCap,
  FaIdCard,
  FaInfoCircle,
  FaKey,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaSave,
  FaPaperPlane,
  FaShieldAlt,
  FaTimes,
  FaUser,
  FaUserEdit,
} from 'react-icons/fa';
import { arrayUnion, collection, doc, getDocs, getFirestore, query, updateDoc, where } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { app } from '../../firebase/firebase';
import { AppContext } from '../../Context';
import { actualizarClasificacionAlumno } from '../../services/clasificacionAlumnos';
import { esAlumno } from '../../utils/clasificacionAlumnos';

const MySwal = withReactContent(Swal);

const STATUS_OPTIONS = [
  { value: '', label: 'Seleccionar' },
  { value: 'Activo', label: 'Activo' },
  { value: 'Inactivo', label: 'Inactivo' },
  { value: 'Baja Temporal', label: 'Baja temporal' },
  { value: 'Baja Definitiva', label: 'Baja definitiva' },
  { value: 'Graduado / Certificado', label: 'Graduado / Certificado' },
];

const LEVEL_OPTIONS = [
  { value: '', label: 'Seleccionar' },
  { value: '1', label: 'Nivel 1' },
  { value: '2', label: 'Nivel 2' },
  { value: '3', label: 'Nivel 3' },
];

const normalizeUserStatus = (value = '') => {
  const normalized = value.toString().trim().toLowerCase();
  if (normalized === 'activos') return 'Activo';
  if (normalized.includes('graduado') || normalized.includes('certificado')) return 'Graduado / Certificado';
  if (normalized === 'baja temporalidad') return 'Baja Temporal';
  return value;
};

const normalizeEmail = (value = '') => value.toString().trim().toLowerCase();

const getInitials = (name = '') => {
  const initials = name
    .toString()
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('');

  return (initials || 'U').toUpperCase();
};

const useTeachers = () => {
  const [teachers, setTeachers] = useState([]);
  const db = getFirestore();

  useEffect(() => {
    const loadTeachers = async () => {
      try {
        const q = query(collection(db, 'users'), where('teacher', '==', true));
        const querySnapshot = await getDocs(q);
        const loadedTeachers = querySnapshot.docs.map((teacherDoc) => ({
          id: teacherDoc.id,
          ...teacherDoc.data(),
        }));
        setTeachers(loadedTeachers);
      } catch (error) {
        console.error('Error fetching teachers: ', error);
      }
    };

    loadTeachers();
  }, [db]);

  return teachers;
};

const EditUserModal = ({ show, handleClose, user }) => {
  const { currentUser } = useContext(AppContext);
  const puedeClasificar = currentUser?.isSuperAdmin === true && esAlumno(user);
  const teachers = useTeachers();
  const [changingPassword, setChangingPassword] = useState(false);
  const [sendingInvitation, setSendingInvitation] = useState(false);
  const [savingUser, setSavingUser] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [editedUser, setEditedUser] = useState({
    userid: '',
    displayName: '',
    username: '',
    email: '',
    teacherId: '',
    asignTeacher: '',
    isMaster: false,
    estatus: '',
    comentario: '',
    name: '',
    matricula: '',
    inscripcion: '',
    estrategia: '',
    date: '',
    age: '',
    country: '',
    city: '',
    phone: '',
    other: '',
    zone: '',
    level: '',
    activity: '',
    password: '',
    usuarioPrueba: false,
  });
  const needsAuthAccess = user?.authAccessStatus !== 'complete';

  useEffect(() => {
    if (user) {
      setShowPassword(false);
      const normalizedLevel = user.level?.replace('Nivel ', '') || '';

      setEditedUser({
        userid: user.id || '',
        displayName: user.displayName || '',
        username: user.username || '',
        email: user.email || '',
        teacherId: user.teacherId || '',
        asignTeacher: user.asignTeacher || '',
        isMaster: user.isMaster || false,
        estatus: normalizeUserStatus(user.estatus),
        comentario: user.comentarioEstatus || '',
        name: user.name || '',
        matricula: user.matricula || '',
        inscripcion: user.inscripcion || '',
        estrategia: user.estrategia || '',
        date: user.date || '',
        age: user.age || '',
        country: user.country || '',
        city: user.city || '',
        phone: user.phone || '',
        other: user.other || '',
        zone: user.zone || '',
        level: normalizedLevel,
        activity: user.activity || '',
        password: '',
        usuarioPrueba: user.usuarioPrueba === true,
      });
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditedUser((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleTeacherChange = (e) => {
    const teacherId = e.target.value;
    const teacher = teachers.find((teacherItem) => teacherItem.id === teacherId);
    setEditedUser((prev) => ({
      ...prev,
      teacherId,
      asignTeacher: teacher ? teacher.displayName : '',
    }));
  };

  const handleSave = async () => {
    const db = getFirestore();
    const userRef = doc(db, 'users', editedUser.userid);

    const updatedData = {
      ...(editedUser.displayName && { displayName: editedUser.displayName }),
      ...(editedUser.username && { username: editedUser.username }),
      ...(editedUser.email && { email: editedUser.email }),
      ...(editedUser.teacherId && { teacherId: editedUser.teacherId }),
      ...(editedUser.asignTeacher && { asignTeacher: editedUser.asignTeacher }),
      ...(editedUser.isMaster !== undefined && { isMaster: editedUser.isMaster }),
      ...(editedUser.estatus && { estatus: editedUser.estatus }),
      ...(editedUser.comentario && { comentarioEstatus: editedUser.comentario }),
      ...(editedUser.name && { name: editedUser.name }),
      ...(editedUser.matricula && { matricula: editedUser.matricula }),
      ...(editedUser.inscripcion && { inscripcion: editedUser.inscripcion }),
      ...(editedUser.estrategia && { estrategia: editedUser.estrategia }),
      ...(editedUser.date && { date: editedUser.date }),
      ...(editedUser.age && { age: editedUser.age }),
      ...(editedUser.country && { country: editedUser.country }),
      ...(editedUser.city && { city: editedUser.city }),
      ...(editedUser.phone && { phone: editedUser.phone }),
      ...(editedUser.other && { other: editedUser.other }),
      ...(editedUser.zone && { zone: editedUser.zone }),
      ...(editedUser.level && { level: editedUser.level }),
      ...(editedUser.activity && { activity: editedUser.activity }),
    };

    if (editedUser.estatus && editedUser.estatus !== normalizeUserStatus(user?.estatus)) {
      updatedData.historialEstatus = arrayUnion({
        estatusAnterior: user?.estatus || 'Sin estatus',
        estatusNuevo: editedUser.estatus,
        comentario: editedUser.comentario.trim(),
        fecha: new Date().toISOString(),
        actualizadoPor: getAuth().currentUser?.uid || '',
      });
    }

    try {
      setSavingUser(true);
      if (user?.authAccessStatus !== 'missing-auth') {
        const functions = getFunctions(app, 'us-central1');
        const updateUserIdentityByAdmin = httpsCallable(functions, 'updateUserIdentityByAdmin');
        await updateUserIdentityByAdmin({
          uid: editedUser.userid,
          email: editedUser.email,
          displayName: editedUser.displayName || editedUser.name,
        });
      }
      await updateDoc(userRef, updatedData);
      if (puedeClasificar && editedUser.usuarioPrueba !== (user.usuarioPrueba === true)) {
        await actualizarClasificacionAlumno({ uid: editedUser.userid, usuarioPrueba: editedUser.usuarioPrueba });
      }
      await MySwal.fire({
        icon: 'success',
        title: 'Usuario actualizado',
        text: 'Los cambios se guardaron correctamente.',
        confirmButtonText: 'OK',
      });
      handleClose();
    } catch (error) {
      console.error('Error al actualizar el usuario: ', error);
      let errorMessage = 'Ocurrió un error al intentar actualizar la información.';
      if (error?.code === 'functions/already-exists') {
        errorMessage = 'El correo ya pertenece a otra cuenta de acceso.';
      } else if (error?.code === 'functions/failed-precondition') {
        errorMessage = error?.message || 'Primero debes completar la cuenta de acceso.';
      } else if (error?.code === 'functions/permission-denied') {
        errorMessage = 'No tienes permisos para actualizar este usuario.';
      }
      await MySwal.fire({
        icon: 'error',
        title: 'Error',
        text: errorMessage,
      });
    } finally {
      setSavingUser(false);
    }
  };

  const handleChangePassword = async () => {
    const newPassword = editedUser.password || '';
    if (!newPassword || newPassword.length < 6) {
      await MySwal.fire({
        icon: 'warning',
        title: 'Contraseña inválida',
        text: 'La contraseña debe tener al menos 6 caracteres.',
      });
      return;
    }

    const confirm = await MySwal.fire({
      icon: 'warning',
      title: 'Cambiar contraseña',
      text: 'Se actualizará la contraseña de este usuario.',
      showCancelButton: true,
      confirmButtonText: 'Cambiar',
      cancelButtonText: 'Cancelar',
    });

    if (!confirm.isConfirmed) return;

    try {
      setChangingPassword(true);
      const functions = getFunctions(app, 'us-central1');
      const updateUserPasswordByAdmin = httpsCallable(functions, 'updateUserPasswordByAdmin');
      const result = await updateUserPasswordByAdmin({ uid: editedUser.userid, password: newPassword });
      const accessCreated = result?.data?.accessCreated === true;

      await MySwal.fire({
        icon: 'success',
        title: accessCreated ? 'Acceso creado' : 'Contraseña actualizada',
        text: accessCreated
          ? 'La cuenta de acceso fue creada y ya puede iniciar sesión.'
          : 'La contraseña fue actualizada correctamente.',
        confirmButtonText: 'OK',
      });

      setEditedUser((prev) => ({ ...prev, password: '' }));
    } catch (error) {
      console.error('Error cambiando contraseña:', error);
      let errorMessage = 'No se pudo cambiar la contraseña.';
      if (error?.code === 'functions/permission-denied') {
        errorMessage = 'No tienes permisos para cambiar contraseñas.';
      } else if (error?.code === 'functions/unauthenticated') {
        errorMessage = 'Debes iniciar sesión para cambiar contraseñas.';
      } else if (error?.code === 'functions/invalid-argument') {
        errorMessage = 'La contraseña es inválida.';
      } else if (error?.code === 'functions/not-found') {
        errorMessage = 'No se encontró el usuario.';
      } else if (error?.code === 'functions/already-exists') {
        errorMessage = 'El correo está asociado a otra cuenta de acceso.';
      } else if (error?.code === 'functions/failed-precondition') {
        errorMessage = error?.message || 'El perfil no tiene la información necesaria para crear el acceso.';
      }

      await MySwal.fire({
        icon: 'error',
        title: 'Error',
        text: errorMessage,
      });
    } finally {
      setChangingPassword(false);
    }
  };

  const handleResendWelcomeInvitation = async () => {
    const currentEmail = normalizeEmail(user?.email);
    const editedEmail = normalizeEmail(editedUser.email);

    if (!currentEmail) {
      await MySwal.fire({
        icon: 'warning',
        title: 'Correo no disponible',
        text: 'Este usuario no tiene un correo registrado para recibir la invitación.',
      });
      return;
    }

    if (editedEmail !== currentEmail) {
      await MySwal.fire({
        icon: 'info',
        title: 'Guarda el nuevo correo primero',
        text: 'Guarda los cambios antes de reenviar la invitación para enviarla al correo actualizado.',
      });
      return;
    }

    const confirm = await MySwal.fire({
      icon: 'question',
      title: 'Reenviar bienvenida',
      text: `Se enviará una invitación a ${currentEmail} para que el usuario cree o cambie su contraseña.`,
      showCancelButton: true,
      confirmButtonText: 'Enviar invitación',
      cancelButtonText: 'Cancelar',
    });

    if (!confirm.isConfirmed) return;

    try {
      setSendingInvitation(true);
      const functions = getFunctions(app, 'us-central1');
      const sendWelcomeInvitationByAdmin = httpsCallable(functions, 'sendWelcomeInvitationByAdmin');
      const result = await sendWelcomeInvitationByAdmin({ uid: editedUser.userid });
      const sentEmail = result?.data?.email || currentEmail;

      await MySwal.fire({
        icon: 'success',
        title: 'Invitación enviada',
        text: `Se envió el correo de bienvenida a ${sentEmail}.`,
      });
    } catch (error) {
      console.error('Error reenviando invitación:', error);
      await MySwal.fire({
        icon: 'error',
        title: 'No se pudo enviar la invitación',
        text: error?.message || 'Inténtalo nuevamente más tarde.',
      });
    } finally {
      setSendingInvitation(false);
    }
  };

  const displayName = editedUser.displayName || editedUser.name || 'Usuario sin nombre';

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      scrollable
      size="xl"
      dialogClassName="master-user-modal-dialog master-user-modal-dialog--edit"
      contentClassName="master-user-modal"
    >
      <Modal.Header closeButton className="master-user-modal__header">
        <div className="master-user-modal__titlebar">
          <span className="master-user-modal__icon">
            <FaUserEdit />
          </span>
          <div>
            <span className="master-user-modal__eyebrow">Panel master</span>
            <Modal.Title>Editar usuario</Modal.Title>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body className="master-user-modal__body">
        <div className="master-user-modal__profile">
          <div className="master-user-modal__avatar">{getInitials(displayName)}</div>
          <div className="master-user-modal__profile-copy">
            <h3>{displayName}</h3>
            <span>{editedUser.email || 'Sin correo registrado'}</span>
          </div>
          <div className="master-user-modal__status">
            <span>{editedUser.estatus || 'Sin estatus'}</span>
            <small>{editedUser.asignTeacher || 'Sin profesor asignado'}</small>
          </div>
        </div>

        <Form className="master-modal-form" onSubmit={(e) => e.preventDefault()}>
          <section className="master-modal-section">
            <div className="master-modal-section__heading">
              <FaIdCard />
              <h3>Identidad</h3>
            </div>
            <div className="master-modal-form__grid">
              <Form.Group className="master-modal-field" controlId="userid">
                <Form.Label>ID de usuario</Form.Label>
                <InputGroup>
                  <InputGroup.Text>
                    <FaIdCard />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    name="userid"
                    value={editedUser.userid}
                    onChange={handleChange}
                    disabled
                  />
                </InputGroup>
              </Form.Group>

              <Form.Group className="master-modal-field" controlId="displayName">
                <Form.Label>Nombre visible</Form.Label>
                <InputGroup>
                  <InputGroup.Text>
                    <FaUser />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    name="displayName"
                    value={editedUser.displayName}
                    onChange={handleChange}
                  />
                </InputGroup>
              </Form.Group>

              <Form.Group className="master-modal-field" controlId="username">
                <Form.Label>Usuario</Form.Label>
                <InputGroup>
                  <InputGroup.Text>
                    <FaUser />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    name="username"
                    value={editedUser.username}
                    onChange={handleChange}
                  />
                </InputGroup>
              </Form.Group>

              <Form.Group className="master-modal-field" controlId="email">
                <Form.Label>Correo electrónico</Form.Label>
                <InputGroup>
                  <InputGroup.Text>
                    <FaEnvelope />
                  </InputGroup.Text>
                  <Form.Control
                    type="email"
                    name="email"
                    value={editedUser.email}
                    onChange={handleChange}
                  />
                </InputGroup>
              </Form.Group>
            </div>
          </section>

          <section className="master-modal-section">
            <div className="master-modal-section__heading">
              <FaChalkboardTeacher />
              <h3>Rol y seguimiento</h3>
            </div>
            <div className="master-modal-form__grid">
              <Form.Group className="master-modal-field" controlId="formTeacherSelect">
                <Form.Label>Profesor asignado</Form.Label>
                <Form.Select name="teacherId" value={editedUser.teacherId} onChange={handleTeacherChange}>
                  <option value="">Ninguno</option>
                  {teachers.map((teacher) => (
                    <option key={teacher.id} value={teacher.id}>{teacher.displayName}</option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="master-modal-field" controlId="formEstatus">
                <Form.Label>Estado</Form.Label>
                <Form.Select name="estatus" value={editedUser.estatus} onChange={handleChange}>
                  {STATUS_OPTIONS.map((option) => (
                    <option key={option.value || 'empty'} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="master-modal-field" controlId="level">
                <Form.Label>Nivel</Form.Label>
                <Form.Select name="level" value={editedUser.level || ''} onChange={handleChange}>
                  {LEVEL_OPTIONS.map((option) => (
                    <option key={option.value || 'empty'} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="master-modal-field master-modal-field--switch" controlId="isMaster">
                <Form.Check
                  type="switch"
                  label="Usuario master"
                  name="isMaster"
                  checked={editedUser.isMaster}
                  onChange={handleChange}
                />
                <FaShieldAlt />
              </Form.Group>

              <Form.Group className="master-modal-field master-modal-field--full" controlId="comentario">
                <Form.Label>Comentario de estado</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="comentario"
                  value={editedUser.comentario || ''}
                  onChange={handleChange}
                />
                <Form.Text className="text-muted">
                  Cada cambio de estado se añade al historial interno del alumno.
                </Form.Text>
              </Form.Group>
            </div>
          </section>

          <section className="master-modal-section">
            <div className="master-modal-section__heading">
              <FaGraduationCap />
              <h3>Datos académicos</h3>
            </div>
            {esAlumno(user) && (
              <Form.Group className="mb-3" controlId="usuarioPrueba">
                <Form.Check type="checkbox" name="usuarioPrueba" label="Usuario de prueba"
                  checked={editedUser.usuarioPrueba} onChange={handleChange} disabled={!puedeClasificar} />
                <Form.Text className="text-muted">
                  No cuenta como alumno real ni en las métricas. Sólo el superadministrador puede cambiar esta etiqueta.
                </Form.Text>
              </Form.Group>
            )}
            <div className="master-modal-form__grid">
              <Form.Group className="master-modal-field" controlId="name">
                <Form.Label>Nombre legal</Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={editedUser.name || ''}
                  onChange={handleChange}
                />
              </Form.Group>

              <Form.Group className="master-modal-field" controlId="matricula">
                <Form.Label>Matrícula</Form.Label>
                <Form.Control
                  type="text"
                  name="matricula"
                  value={editedUser.matricula || ''}
                  onChange={handleChange}
                />
              </Form.Group>

              <Form.Group className="master-modal-field" controlId="inscripcion">
                <Form.Label>Inscripción</Form.Label>
                <Form.Control
                  type="text"
                  name="inscripcion"
                  value={editedUser.inscripcion || ''}
                  onChange={handleChange}
                />
              </Form.Group>

              <Form.Group className="master-modal-field" controlId="estrategia">
                <Form.Label>Estrategia</Form.Label>
                <Form.Control
                  type="text"
                  name="estrategia"
                  value={editedUser.estrategia || ''}
                  onChange={handleChange}
                />
              </Form.Group>

              <Form.Group className="master-modal-field master-modal-field--full" controlId="activity">
                <Form.Label>Actividad</Form.Label>
                <Form.Control
                  type="text"
                  name="activity"
                  value={editedUser.activity || ''}
                  onChange={handleChange}
                />
              </Form.Group>
            </div>
          </section>

          <section className="master-modal-section">
            <div className="master-modal-section__heading">
              <FaInfoCircle />
              <h3>Información personal</h3>
            </div>
            <div className="master-modal-form__grid">
              <Form.Group className="master-modal-field" controlId="date">
                <Form.Label>Fecha de nacimiento</Form.Label>
                <InputGroup>
                  <InputGroup.Text>
                    <FaCalendarAlt />
                  </InputGroup.Text>
                  <Form.Control
                    type="date"
                    name="date"
                    value={editedUser.date || ''}
                    onChange={handleChange}
                  />
                </InputGroup>
              </Form.Group>

              <Form.Group className="master-modal-field" controlId="age">
                <Form.Label>Edad</Form.Label>
                <Form.Control
                  type="number"
                  name="age"
                  value={editedUser.age || ''}
                  onChange={handleChange}
                />
              </Form.Group>

              <Form.Group className="master-modal-field" controlId="country">
                <Form.Label>País</Form.Label>
                <InputGroup>
                  <InputGroup.Text>
                    <FaMapMarkerAlt />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    name="country"
                    value={editedUser.country || ''}
                    onChange={handleChange}
                  />
                </InputGroup>
              </Form.Group>

              <Form.Group className="master-modal-field" controlId="city">
                <Form.Label>Ciudad</Form.Label>
                <InputGroup>
                  <InputGroup.Text>
                    <FaMapMarkerAlt />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    name="city"
                    value={editedUser.city || ''}
                    onChange={handleChange}
                  />
                </InputGroup>
              </Form.Group>

              <Form.Group className="master-modal-field" controlId="phone">
                <Form.Label>Teléfono</Form.Label>
                <InputGroup>
                  <InputGroup.Text>
                    <FaPhoneAlt />
                  </InputGroup.Text>
                  <Form.Control
                    type="tel"
                    name="phone"
                    value={editedUser.phone || ''}
                    onChange={handleChange}
                  />
                </InputGroup>
              </Form.Group>

              <Form.Group className="master-modal-field" controlId="zone">
                <Form.Label>Zona horaria</Form.Label>
                <Form.Control
                  type="text"
                  name="zone"
                  value={editedUser.zone || ''}
                  onChange={handleChange}
                />
              </Form.Group>

              <Form.Group className="master-modal-field master-modal-field--full" controlId="other">
                <Form.Label>Datos adicionales</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="other"
                  value={editedUser.other || ''}
                  onChange={handleChange}
                />
              </Form.Group>
            </div>
          </section>

          <section className="master-modal-section">
            <div className="master-modal-section__heading">
              <FaKey />
              <h3>Seguridad</h3>
            </div>
            {needsAuthAccess && (
              <div className="master-auth-warning" role="alert">
                Este perfil no tiene una cuenta de acceso. Define una contraseña para crearla con el mismo correo e ID.
              </div>
            )}
            <Form.Group className="master-modal-field" controlId="password">
              <Form.Label>Contraseña</Form.Label>
              <InputGroup>
                <InputGroup.Text>
                  <FaKey />
                </InputGroup.Text>
                <Form.Control
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={editedUser.password || ''}
                  onChange={handleChange}
                  autoComplete="new-password"
                />
                <Button
                  type="button"
                  variant="outline-secondary"
                  aria-label={showPassword ? 'Ocultar contraseña nueva' : 'Mostrar contraseña nueva'}
                  title={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  onClick={() => setShowPassword((currentValue) => !currentValue)}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </Button>
                <Button
                  type="button"
                  variant="outline-primary"
                  onClick={handleChangePassword}
                  disabled={!editedUser.password || changingPassword}
                >
                  {changingPassword ? (
                    <>
                      <Spinner animation="border" size="sm" />
                      Cambiando...
                    </>
                  ) : (
                    needsAuthAccess ? 'Crear acceso' : 'Cambiar'
                  )}
                </Button>
              </InputGroup>
              <Form.Text className="text-muted">
                Solo puedes ver la contraseña nueva que estás escribiendo; la contraseña actual nunca se muestra.
              </Form.Text>
            </Form.Group>
            <Button
              type="button"
              variant="outline-primary"
              className="mt-3"
              onClick={handleResendWelcomeInvitation}
              disabled={sendingInvitation || needsAuthAccess}
            >
              {sendingInvitation ? (
                <>
                  <Spinner animation="border" size="sm" />
                  Enviando...
                </>
              ) : (
                <>
                  <FaPaperPlane /> Reenviar bienvenida
                </>
              )}
            </Button>
            {needsAuthAccess && (
              <Form.Text className="text-muted d-block mt-2">
                La invitación estará disponible cuando se cree la cuenta de acceso.
              </Form.Text>
            )}
          </section>
        </Form>
      </Modal.Body>

      <Modal.Footer className="master-user-modal__footer">
        <Button
          type="button"
          variant="outline-primary"
          className="btn-transition"
          onClick={handleClose}
        >
          <FaTimes /> Cancelar
        </Button>
        <Button
          type="button"
          variant="primary"
          className="btn-transition"
          onClick={handleSave}
          disabled={savingUser}
        >
          {savingUser ? (
            <>
              <Spinner animation="border" size="sm" />
              Guardando...
            </>
          ) : (
            <>
              <FaSave /> Guardar cambios
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditUserModal;
